import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AuthGate from "@/components/AuthGate";
import OrgStatsTable, { type PortalCompany } from "@/components/peo/OrgStatsTable";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/company-dashboard")({
  head: () => ({
    meta: [
      { title: "Company dashboard — Home Approach" },
      { name: "description", content: "Aggregate Home Approach engagement for your company." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Company dashboard — Home Approach" },
      { property: "og:description", content: "Aggregate Home Approach engagement for your company." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: () => <AuthGate><CompanyDashboard /></AuthGate>,
});

type HrCompany = PortalCompany & { peo_slug: string | null };

function announcement(c: HrCompany) {
  const link = `https://www.homeapproach.com/${c.peo_slug ?? "request-access"}`;
  return `Subject: A new benefit to help you buy, or care for, your home

Hi team,

We're now offering Home Approach, a free benefit that helps you see how ready you are to buy, find down payment help you may qualify for, and get guidance from today through closing. Already own a home? There are tools for you too.

Join here: ${link}
Your company code: ${c.code}

Your privacy is protected: we only ever see overall totals, never who signed up or anything you share.`;
}

function CompanyDashboard() {
  const q = useQuery({
    queryKey: ["hr-portal"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_hr_portal");
      if (error) throw error;
      return (data as unknown as HrCompany[]) ?? [];
    },
  });
  const [copied, setCopied] = useState<string | null>(null);
  if (q.isLoading) return <div className="p-10 text-muted-foreground">Loading…</div>;
  const list = q.data ?? [];
  if (!list.length) return <div className="mx-auto max-w-xl p-10 text-foreground">This account isn't linked to a company yet. Contact partnerships@homeapproach.com.</div>;
  return (
    <main className="mx-auto max-w-5xl px-5 py-10">
      <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: "var(--heading-font)" }}>Company dashboard</h1>
      <section className="mt-6"><OrgStatsTable companies={list} /></section>
      {list.map((c) => (
        <section key={c.id} className="mt-8 rounded-xl border bg-card p-5">
          <h2 className="font-semibold text-foreground">Launch kit for {c.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">Copy this announcement into an email or your benefits portal.</p>
          <pre className="mt-3 whitespace-pre-wrap rounded bg-muted p-3 text-sm text-foreground">{announcement(c)}</pre>
          <Button
            className="mt-3"
            onClick={() => { navigator.clipboard.writeText(announcement(c)); setCopied(c.id); }}
          >
            {copied === c.id ? "Copied" : "Copy announcement"}
          </Button>
        </section>
      ))}
    </main>
  );
}
